import React, { useState, useMemo } from "react";
import worldCountries from 'world-countries';
import {
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  Grid,
  Divider,
  Autocomplete,
  Snackbar,
  Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { Layout as DashboardLayout } from 'layouts/portal/layout';
import { useNavigate } from 'react-router-dom';
import { add_user } from 'components/Auth';

export const AddUser = () => {
  const navigate = useNavigate();
  
  // Process world countries data to create our country codes array
  const COUNTRY_CODES = useMemo(() => {
    return worldCountries
      .filter(country => country.idd?.root)
      .map(country => {
        let callingCode = country.idd.root.replace('+', '');
        
        if (country.idd.suffixes && country.idd.suffixes.length > 0) {
          if (country.idd.suffixes.length < 10) {
            callingCode += country.idd.suffixes[0];
          }
        }
        
        return {
          code: country.cca2,
          name: country.name.common,
          callingCode: callingCode,
          flag: country.flag
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name)) // Sort alphabetically by name
      .filter((country, index, arr) => {
        // Remove duplicates based on country code
        return index === arr.findIndex(c => c.code === country.code);
      });
  }, []);

  const formatCountryDisplay = (country) => {
    return `${country.flag}    ${country.name} (+${country.callingCode})`;
  };
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    countryCode: 'DK', // Default to DK
    synchronizeLists: true, // Default checked like iOS
    restrictBudget: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success', 'error', 'warning', 'info'
  });

  const selectedCountry = COUNTRY_CODES.find(country => country.code === formData.countryCode) || COUNTRY_CODES.find(country => country.code === 'DK');

  const handleInputChange = (field) => (event) => {
    let value = event.target.value;
    
    // Special handling for phone number - clean and format like iOS
    if (field === 'phoneNumber') {
      // Remove all non-digit characters
      value = value.replace(/\D/g, '');
      
      // Limit length (most phone numbers are 10-15 digits)
      if (value.length > 15) {
        value = value.substring(0, 15);
      }
    }
    
    setFormData({
      ...formData,
      [field]: value
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: null
      });
    }
  };

  const handleCountryChange = (event, newValue) => {
    setFormData({
      ...formData,
      countryCode: newValue ? newValue.code : 'DK'
    });

    // Clear error when user makes selection
    if (errors.countryCode) {
      setErrors({
        ...errors,
        countryCode: null
      });
    }
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.checked
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required before you can invite. Please try again.';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required before you can invite. Please try again.';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Sorry, the email address you entered does not appear to be properly formatted. Please try again.';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'A phone number is required to link a user to this account.';
    }

    if (!selectedCountry?.callingCode) {
      newErrors.countryCode = 'A country code is required to link a user to this account.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Combine country code and phone number like iOS: "+\(countryCode)\(phoneNumber)"
      const fullPhoneNumber = `+${selectedCountry.callingCode}${formData.phoneNumber}`;
      
      // Process privileges like iOS
      const privileges = {
        useOwnerLists: formData.synchronizeLists,
        restrictBudgets: formData.restrictBudget
      };

      // Prepare data for Firebase function call (matching cloud function expectations)
      const userData = {
        email: formData.email.toLowerCase().trim(),
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber, // Local number only 
        countryCode: selectedCountry.callingCode, // Calling code like "45"
        userRole: formData.restrictBudget ? 4 : 3,
        useOwnerLists: formData.synchronizeLists, // Match iOS privilege naming
        restrictBudgets: formData.restrictBudget // Additional privilege (if supported)
      };

      // Call Firebase function
      const result = await add_user(userData);      
      setSubmitSuccess(true);
      
      // Show success notification
      setSnackbar({
        open: true,
        message: `Invitation sent successfully to ${formData.firstName} ${formData.lastName} at ${formData.email.toLowerCase().trim()}`,
        severity: 'success'
      });
      
      // Navigate back to users list after successful invitation
      setTimeout(() => {
        navigate('/users');
      }, 2000); // Give user time to see the success message

    } catch (error) {
      console.error('Error adding user:', error);

      let errorMessage = error.message || 'An error occurred while sending the invitation. Please try again.';

      // Show error notification only (not duplicate in form)
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/users');
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <DashboardLayout>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <Typography variant="h2">
          Add User
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Invite a new user to access your store.
        </Typography>
      </Box>

      <Card sx={{ maxWidth: 800 }}>
        <CardContent>
          <Grid container spacing={3}>
            {/* First Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={handleInputChange('firstName')}
                error={!!errors.firstName}
                helperText={errors.firstName}
                required
              />
            </Grid>

            {/* Last Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={handleInputChange('lastName')}
                error={!!errors.lastName}
                helperText={errors.lastName}
                required
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                error={!!errors.email}
                helperText={errors.email}
                required
              />
            </Grid>

            {/* Country Code */}
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={COUNTRY_CODES}
                value={selectedCountry}
                onChange={handleCountryChange}
                getOptionLabel={(option) => formatCountryDisplay(option)}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Typography variant="body2">
                      {formatCountryDisplay(option)}
                    </Typography>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country"
                    placeholder="Type country name or code..."
                    error={!!errors.countryCode}
                    helperText={errors.countryCode}
                  />
                )}
                filterOptions={(options, { inputValue }) => {
                  return options.filter(option => 
                    option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                    option.code.toLowerCase().includes(inputValue.toLowerCase()) ||
                    option.callingCode.includes(inputValue.replace('+', ''))
                  );
                }}
                isOptionEqualToValue={(option, value) => option.code === value.code}
              />
            </Grid>

            {/* Phone Number */}
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Mobile Number"
                value={formData.phoneNumber}
                onChange={handleInputChange('phoneNumber')}
                error={!!errors.phoneNumber}
                placeholder="12345678"
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*'
                }}
                required
              />
            </Grid>

            {/* Permissions Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>
                User Permissions
              </Typography>
            </Grid>

            {/* Synchronize Lists */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.synchronizeLists}
                    onChange={handleCheckboxChange('synchronizeLists')}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      Synchronize Lists
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select this option to synchronize brands, categories and colors with this user.
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            {/* Restrict Budget */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.restrictBudget}
                    onChange={handleCheckboxChange('restrictBudget')}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">
                      Restrict Budget Access
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select this option to prevent this user from seeing the budget screen.
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handleSubmit}
                  disabled={isSubmitting || submitSuccess}
                  size="large"
                >
                  {isSubmitting ? 'Sending Invitation...' : submitSuccess ? 'Invitation Sent!' : 'Send Invitation'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};
